/*eslint max-lines: [2, { "max": 1700, "skipComments": true, "skipBlankLines": true}]*/

import _ from 'lodash';
import experiment from 'experiment';
import constants from '#packages/constants';
import platformConsts from '../../common/constants';

import * as stateManagement from '#packages/stateManagement';
import * as platform from '#packages/platform';
import * as sectionsService from '../../services/sectionsService';
import * as editorUtils from './utils/utils';

import {
  themeToCssVarName,
  cssVarNameToTheme,
} from '@wix/editor-platform-host-integration/ui';
import { isNewColorPaletteOpen } from '#packages/theme';
import {
  pendingAppsService,
  constants as tpaConstants,
  appMarketUtils,
  tpaAlertsService,
  bi,
} from '#packages/tpa';
import { fedopsLogger, sections as sectionsUtil } from '#packages/util';
import { translate } from '#packages/i18n';
import { getPanelProps } from '../../common/utils';
import {
  navigateAndOpenPanel,
  createOpenTpaComponentPanelHandler,
} from './panelHelpers/panelHelpers';

import type { AppData, CompRef, Point } from 'types/documentServices';
import {
  EditorPlatformHostIntegrationAPI,
  type OnFontChanged,
  type FontStyle,
  type FieldAccessibility,
  type FontPickerOptions,
  type FontPickerOnStyleChange,
} from '@wix/editor-platform-host-integration-apis';
import type {
  AddComponentHandler,
  AppData as PlatformAppData,
  ComponentRef,
  RemoveComponentHandler,
  EditorNamespace,
  NativePanelType,
  ComponentPanelsConfigurations,
  AddElementsPanelOptions,
} from '@wix/platform-editor-sdk';
import type { EditorAPI } from '#packages/editorAPI';
import type { Notification } from '#packages/stateManagement';
import type { ColorName } from '#packages/theme';
import type {
  OpenConfirmationPanelOptions,
  OpenPromotionalPanelOptions,
  OpenErrorPanelOptions,
} from '../../panels/platformPanelsUtils';
import type {
  OpenNativeComponentPanelOptions,
  ShowUserActionNotificationOptions,
} from './editorInterfaces';

const { openPlatformPanel, updateOrOpenPanel } = stateManagement.panels.actions;

const mediaPayloadConverterByType = {
  IMAGE(payload: AnyFixMe) {
    return {
      uri: payload.uri,
      title: payload.title,
      width: payload.width,
      height: payload.height,
    };
  },
  DOCUMENT(payload: AnyFixMe) {
    return {
      uri: payload.uri,
      title: payload.title,
      fileUrl: payload.fileUrl,
    };
  },
  DEFAULT(payload: AnyFixMe) {
    return payload;
  },
};

function openNativeComponentPanel<
  ComponentType extends keyof ComponentPanelsConfigurations,
  NativePanel extends
    NativePanelType<ComponentType> = NativePanelType<ComponentType>,
>(
  editorAPI: EditorAPI,
  appData: AppData,
  nativePanelType: NativePanel | string,
  {
    componentRef,
    panelSectionsDefinition,
    helpId,
    configuration,
  }: OpenNativeComponentPanelOptions<ComponentType, NativePanel>,
) {
  return new Promise((resolve, reject) => {
    const nativePanelName =
      platformConsts.NATIVE_PANELS[
        nativePanelType as keyof typeof platformConsts.NATIVE_PANELS
      ];

    if (!nativePanelName) {
      return reject(
        new Error(`panelType ${nativePanelType as string} unknown`),
      );
    }
    if (!componentRef) {
      return reject(new Error('No componentRef provided'));
    }
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/map
    const allConnections = _.map(
      (editorAPI as AnyFixMe).dsRead.platform.controllers.connections.get(
        componentRef,
      ),
      'controllerRef',
    );

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/filter
    const applicationConnections = _.filter(
      allConnections,
      (controllerRef: AnyFixMe) =>
        editorAPI.dsRead.components.data.get(controllerRef).applicationId ===
        appData.appDefinitionId,
    );
    if (_.isEmpty(applicationConnections)) {
      return reject(
        new Error(
          `Component ${componentRef.id} is not connected to app ${appData.appDefinitionId}`,
        ),
      );
    }

    const panelSectionsOverride =
      panelSectionsDefinition &&
      (nativePanelType === 'settings' || nativePanelType === 'manage')
        ? { panelSectionsDefinition }
        : {};

    const panelProps = {
      experimentIsOpen: experiment.isOpen,
      helpId,
      ...panelSectionsOverride,
      configuration,
    };

    editorAPI.selection.selectComponentByCompRef(componentRef);
    editorAPI.openComponentPanel(nativePanelName, panelProps, {
      panelMetaData: {
        resolve,
      },
    });
  });
}

type ComponentPanelOptions =
  | Parameters<EditorNamespace['openComponentPanel']>[1]
  | AnyFixMe;

function openComponentPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: ComponentPanelOptions,
  onOpen: (panelToken: string) => void,
) {
  const tpaModalPanelUrl = getTPAModalPanelUrl(
    editorAPI,
    options.url,
    appData.applicationId,
    experiment.isOpen('se_appDefIdUpdates')
      ? appData.appDefinitionId
      : undefined,
  );
  const props = getPanelProps(
    editorAPI,
    appData,
    { scrolling: 'auto', ...options },
    tpaModalPanelUrl,
  );

  if (onOpen) {
    onOpen(props.token);
  }

  editorAPI.selection.selectComponentByCompRef(options.componentRef);
  return new Promise(function (resolve) {
    editorAPI.store.dispatch(
      openPlatformPanel({
        panelName: platformConsts.panelTypes.COMPONENT_PANEL,
        panelProps: props,
        panelMetaData: {
          resolve,
          frameType: constants.PANEL_TYPES.COMP,
          closeAllOtherPanels: true,
        },
      }),
    );
  });
}

function openComponentPanelWithSiteContext(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: ComponentPanelOptions,
  onOpen: (panelToken: string) => void,
) {
  openComponentPanel(
    editorAPI,
    appData,
    token,
    { ...options, useEditorContext: true },
    onOpen,
  );
}

function showPanelPreloader(
  editorAPI: EditorAPI,
  appData: AppData,
  panelToken: string,
) {
  if (
    experiment.isOpen('specs.responsive-editor.disablePlatformPanelPreloader')
  ) {
    return;
  }
  return editorAPI.componentPanelPreloader.showPreloader(panelToken);
}

function hidePanelPreloader(
  editorAPI: EditorAPI,
  appData: AppData,
  panelToken: string,
) {
  if (
    experiment.isOpen('specs.responsive-editor.disablePlatformPanelPreloader')
  ) {
    return;
  }
  return editorAPI.componentPanelPreloader.hidePreloader(panelToken);
}

type OpenChangeVariationsPanelOptions =
  | Parameters<EditorNamespace['openChangeVariationsPanel']>[1]
  | AnyFixMe;

function openChangeVariationsPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: OpenChangeVariationsPanelOptions,
) {
  return new Promise(function (resolve, reject) {
    if (!options.presets) {
      reject(new Error('options must contain presets'));
      return;
    }
    const invalidPresets = options.presets.some(
      (preset: AnyFixMe) =>
        !preset.variationId || !(preset.mainImageUrl || preset.displayName),
    );
    if (invalidPresets) {
      reject(
        new Error(
          'Each preset must contain variationId and mainImageUrl or displayName properties',
        ),
      );
      return;
    }
    const props = getPanelProps(editorAPI, appData, options);
    props.applicationId = appData.applicationId;
    editorAPI.store.dispatch(
      openPlatformPanel({
        panelName: platformConsts.panelTypes.CHANGE_VARIATIONS,
        panelProps: props,
        panelMetaData: {
          resolve,
          frameType: constants.PANEL_TYPES.COMP,
          closeAllOtherPanels: true,
        },
      }),
    );
  });
}

function openModalPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: AnyFixMe,
) {
  const tpaModalPanelUrl = getTPAModalPanelUrl(
    editorAPI,
    options.url,
    appData.applicationId,
    experiment.isOpen('se_appDefIdUpdates')
      ? appData.appDefinitionId
      : undefined,
  );
  const props = getPanelProps(editorAPI, appData, options, tpaModalPanelUrl);
  return new Promise(function (resolve) {
    editorAPI.store.dispatch(
      openPlatformPanel({
        panelName: platformConsts.panelTypes.MODAL,
        panelProps: props,
        panelMetaData: {
          resolve,
        },
      }),
    );
  });
}

function openPagePanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: AnyFixMe,
) {
  const pageId = options.pageRef.id;
  const renameEnabled = options?.renameEnabled || false;
  const dynamicPageSettings =
    editorAPI.pages.dynamicPages.getDynamicPageActions(pageId, 'pageSettings');
  const panelProps = _.isEmpty(dynamicPageSettings)
    ? {
        initialSettingsTabType: constants.PAGE_SETTINGS_TABS.PAGE_INFO,
        renameEnabled,
        origin: options?.origin,
      }
    : {
        initialSettingsTabType: _.head(dynamicPageSettings).type,
        renameEnabled,
        origin: options?.origin,
      };
  editorAPI.navigateAndOpenPagesPanel(pageId, panelProps);
}

function openPagesPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: AnyFixMe,
) {
  const openPanelInteraction = fedopsLogger.mapInteraction(
    fedopsLogger.INTERACTIONS.PAGES_PANEL.OPEN_PANEL,
  );
  const closePanelInteraction = fedopsLogger.mapInteraction(
    fedopsLogger.INTERACTIONS.PAGES_PANEL.CLOSE_PANEL,
  );

  return new Promise((resolve, reject) => {
    const pageId = options.pageRef.id;
    if (!editorAPI.pages.doesPageExist(pageId)) {
      reject('PageRef does not exist'); //eslint-disable-line prefer-promise-reject-errors
      return;
    }
    const panelProps = {
      renameEnabled: options?.renameEnabled,
      initialSettingsTabType: options?.initialSettingsTabType,
      origin: options?.origin,
      openPanelInteraction,
      closePanelInteraction,
      frameType: constants.PANEL_TYPES.LEFT,
    };
    openPanelInteraction.start();
    editorAPI.navigateAndOpenPagesPanel(
      pageId,
      panelProps,
      resolve as () => void,
    );
  });
}

function closePagesPanel(editorAPI: EditorAPI) {
  editorAPI.panelHelpers.closePagesPanel();
}

function canAddStaticPage(editorAPI: EditorAPI) {
  return (editorAPI.pages.canAddStaticPage as never as () => void)();
}

const getOffset = (node: Element) => {
  // https://github.com/madrobby/zepto/blob/master/src/zepto.js#L716
  const { left, top, width, height } = node.getBoundingClientRect();

  return {
    left: left + window.pageXOffset,
    top: top + window.pageYOffset,
    width: Math.round(width),
    height: Math.round(height),
  };
};

function calcPanelPosition(editorAPI: EditorAPI, options: AnyFixMe = {}) {
  const openPanels = editorAPI.panelManager.getOpenPanels();
  // eslint-disable-next-line you-dont-need-lodash-underscore/find
  const platformPanel = _.find(openPanels, { type: 'platform' }) as AnyFixMe;
  if (platformPanel) {
    const iframe = window.document.querySelector(
      `[name="${platformPanel.token as string}"]`,
    );

    if (iframe) {
      const iframePos = getOffset(iframe);
      if (
        iframePos &&
        _.isNumber(iframePos.top) &&
        _.isNumber(iframePos.left)
      ) {
        if (_.isNumber(options.x) && _.isNumber(options.y)) {
          return {
            top: iframePos.top + options.y,
            left: iframePos.left + options.x,
          };
        }
        if (
          _.isNumber(options.height) &&
          platformPanel?.props?.height &&
          platformPanel?.props?.width
        ) {
          return {
            top:
              iframePos.top +
              platformPanel.props.height / 2 -
              options.height / 2,
            left: iframePos.left + platformPanel.props.width / 2,
          };
        }
      }
    }
  }
  return {
    top: '25%',
    left: '45%',
  };
}

function openToolPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: AnyFixMe,
) {
  const props = getPanelProps(editorAPI, appData, options);
  const path = platformConsts.panelTypes.TOOL;
  const position = calcPanelPosition(editorAPI, options);
  _.merge(props, position);

  return new Promise(function (resolve) {
    editorAPI.store.dispatch(
      openPlatformPanel({
        panelName: path,
        panelProps: props,
        panelMetaData: {
          resolve,
        },
      }),
    );
  });
}

function openFloatingToolPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: string,
  options: AnyFixMe,
) {
  const props = getPanelProps(editorAPI, appData, options);

  return new Promise(function (resolve) {
    editorAPI.store.dispatch(
      openPlatformPanel({
        panelName: platformConsts.panelTypes.TOOL_FLOATING,
        panelProps: {
          ...props,
          closeWithUserIntent: true,
        },
        panelMetaData: {
          resolve,
        },
      }),
    );
  });
}

function openSidePanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: AnyFixMe,
) {
  const defaultOptions = {
    displayAboveModals: true,
    closeWithUserIntent: true,
  };
  const props = getPanelProps(editorAPI, appData, {
    ...defaultOptions,
    ...options,
  });

  return new Promise(function (resolve) {
    editorAPI.store.dispatch(
      openPlatformPanel({
        panelName: platformConsts.panelTypes.DOCKED,
        panelProps: props,
        panelMetaData: {
          resolve,
          closeWithUserIntent: props.closeWithUserIntent,
          frameType: constants.PANEL_TYPES.DOCKED_PANEL,
        },
      }),
    );
  });
}

function openLinkPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: AnyFixMe,
) {
  return new Promise(function (resolve) {
    const linkPanelProps = {
      link: options?.value ?? null,
      visibleSections: options?.visibleSections ?? null,
      callback: (value: unknown) => {
        if (options?.callback) {
          options.callback(value);
        }
        resolve(value);
      },
      hideLinkTarget: !options?.showLinkTargetSection,
      onCancelCallback: (value: unknown) => {
        if (options?.callback) {
          options.callback({
            onCancel: true,
          });
        }
        resolve(value);
      },
    };
    editorAPI.openLinkPanel(linkPanelProps);
  });
}

function openColorPicker(
  editorAPI: EditorAPI,
  _appData: AppData,
  _token: string,
  options: AnyFixMe,
  onColorChanged: (options: AnyFixMe) => void,
) {
  const defaultOptions = {
    position: {
      x: -12,
      y: 72,
    },
    color: '#FFFFFF',
  };

  const opts = Object.assign({}, defaultOptions, options);

  let promiseResolve;
  const panelClosedPromise = new Promise((resolve) => {
    promiseResolve = resolve;
  });
  const color = opts.color?.startsWith('--')
    ? cssVarNameToTheme(opts.color)
    : opts.color;

  editorAPI.openColorPicker(
    color,
    calcPanelPosition(editorAPI, opts.position),
    {
      onChange: (color: AnyFixMe) => {
        let hexColor: string | null = null;
        let themeColor = null;

        const colors = editorAPI.theme.colors.getAll() as AnyFixMe;

        if (color.includes('color_')) {
          hexColor = colors[color as ColorName] as string;
          themeColor = color;
        } else {
          hexColor = color;
          themeColor = Object.keys(colors).find(
            (colorName) => colors[colorName as ColorName] === hexColor,
          );
        }

        onColorChanged({
          color: hexColor,
          theme: themeColor,
          cssVariableTheme: themeToCssVarName(themeColor),
          isHover: false,
        });
      },
      onClose: promiseResolve,
      allowPaletteEditing: true,
      enableHistory: false,
      previewOnHover: false,
      overridePosition: true,
      panelMode: 'solid',
    },
  );

  return panelClosedPromise;
}

function openFontPicker(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: FontPickerOptions,
  onStyleChanged: FontPickerOnStyleChange,
  onFontChanged?: OnFontChanged,
): Promise<void> {
  const defaultOptions: Readonly<FontPickerOptions> = {
    position: {
      x: -12,
      y: 72,
    },
    panelSectionsDefinition: {
      theme: 'enabled',
      font: 'enabled',
      size: 'enabled',
      style: 'enabled',
      htmlTag: 'hidden',
    },
  };

  const opts = {
    ...defaultOptions,
    ...options,
    panelSectionsDefinition: {
      ...defaultOptions.panelSectionsDefinition,
      ...options.panelSectionsDefinition,
    },
  };

  const onFontChangedWithCssVar = (
    fieldStyle: FontStyle,
    fieldAccessibility: FieldAccessibility,
  ) => {
    const fontStyle = { ...fieldStyle };
    if (fontStyle.theme && fieldStyle.theme.toLocaleLowerCase() !== 'custom') {
      fontStyle.cssVariableName = themeToCssVarName(fieldStyle.theme);
    }
    return onFontChanged?.(fontStyle, fieldAccessibility);
  };

  const editorKey = options.componentStyle?.editorKey?.startsWith('--')
    ? cssVarNameToTheme(options.componentStyle?.editorKey)
    : options.componentStyle?.editorKey;

  const pickerPosition = options.overridePositon
    ? { top: options.position?.y, left: options.position?.x }
    : calcPanelPosition(editorAPI, opts.position);

  return new Promise((resolve) => {
    editorAPI.panelManager.openPanel(
      'tpaPanels.uiLib.uiLibTextParamDesignPanel',
      Object.assign(
        {
          title: opts.title,
          hideTheme: opts.panelSectionsDefinition.theme === 'hidden',
          hideFont: opts.panelSectionsDefinition.font === 'hidden',
          hideSize: opts.panelSectionsDefinition.size === 'hidden',
          hideStyle: opts.panelSectionsDefinition.style === 'hidden',
          showHTMLTag: opts.panelSectionsDefinition.htmlTag === 'enabled',
          fontMaxSize: opts.fontMaxSize,
          fontMinSize: opts.fontMinSize,
          compStyle: {
            ...opts.componentStyle,
            editorKey,
          },
          onStyleChange: onStyleChanged,
          onFontChanged: onFontChangedWithCssVar,
          onClose: resolve,
        },
        pickerPosition,
      ),
      true,
    );
  });
}

function openMediaPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: AnyFixMe,
) {
  options = _.defaults(options || {}, {
    callOnCancel: true,
    isMultiSelect: true,
    mediaType: 'IMAGE',
  });

  return new Promise(function (resolve) {
    const handleMediaDialogResult = function (mediaDialogPayloads: AnyFixMe) {
      let payload;

      if (options.rawData) {
        payload = mediaDialogPayloads;
      } else if (!_.isEmpty(mediaDialogPayloads)) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line you-dont-need-lodash-underscore/is-array
        if (!_.isArray(mediaDialogPayloads)) {
          mediaDialogPayloads = [mediaDialogPayloads];
        }
        const mediaPayloadConverter = _.get(
          mediaPayloadConverterByType,
          options.mediaType,
          mediaPayloadConverterByType.DEFAULT,
        );
        payload = mediaDialogPayloads.map(mediaPayloadConverter);
      } else if (options.callOnCancel) {
        payload = null;
      }

      resolve(payload);
      editorAPI.mediaServices.setFocusToEditor();
    };
    editorAPI.mediaServices.mediaManager.open(options.mediaType, {
      multiSelect: options.isMultiSelect,
      callback: handleMediaDialogResult,
    });
  });
}

function openHelpPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: AnyFixMe,
) {
  return new Promise((resolve) => {
    const helpPanelProps: {
      origin: string;
      anchorId?: string;
      onClose: (v: unknown) => void;
    } = {
      origin: options.origin || 'platform',
      onClose: resolve,
    } as AnyFixMe;
    const { helpId, anchorId } = options;
    if (anchorId) {
      helpPanelProps.anchorId = anchorId;
    }
    const biHelpParams = {
      component: editorAPI.selection.getSelectedComponentType(),
      learn_more: false,
      panel_name: options.panelName || 'platform',
      origin: options.origin || 'platform',
    };

    editorAPI.panelManager.openHelpCenter(helpId, helpPanelProps, biHelpParams);
  });
}

function getTPAModalPanelUrl(
  editorAPI: EditorAPI,
  url: string | undefined,
  applicationId: number | string | undefined,
  appDefinitionId?: string,
) {
  if (url && applicationId) {
    const selectedCompRef = editorAPI.selection.getSelectedComponents()[0];
    const isTpaByCompType = editorAPI.dsRead.tpa.isTpaByCompType(
      editorAPI.components.getType(selectedCompRef),
    );
    if (!isTpaByCompType) {
      return undefined;
    }

    const allAppComponents = experiment.isOpen('se_appDefIdUpdates')
      ? // @ts-expect-error
        editorAPI.dsRead.tpaV2.app.getAllComps(appDefinitionId)
      : // @ts-expect-error
        editorAPI.dsRead.tpa.app.getAllCompsByApplicationId(
          applicationId as number,
        );
    const isSelectedComponentOwnedByApp = allAppComponents?.some(
      ({ id }: AnyFixMe) => id === selectedCompRef.id,
    );
    if (!isSelectedComponentOwnedByApp) {
      return undefined;
    }

    return editorAPI.dsRead.tpa.app.url.getSettingsModalUrl(
      {
        url,
        deviceType: 'desktop',
        applicationId,
        origCompId: selectedCompRef.id,
      },
      selectedCompRef.id,
    );
  }
}

function openFullStagePanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: AnyFixMe,
) {
  const fullStagePanelProps = getPanelProps(editorAPI, appData, options);
  editorAPI.store.dispatch(
    openPlatformPanel({
      panelName: platformConsts.panelTypes.FULL_STAGE,
      panelProps: fullStagePanelProps,
      panelMetaData: {
        frameType: constants.PANEL_TYPES.FULL_STAGE,
        closeAllOtherPanels: true,
      },
    }),
  );
}

function getPanelFromToken(editorAPI: EditorAPI, token: string) {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/find
  const panelDescriptor = _.find(editorAPI.panelManager.getOpenPanels(), {
    token,
  });

  return panelDescriptor;
}

function updatePanelOptions(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: AnyFixMe,
) {
  const panelDescriptor = getPanelFromToken(editorAPI, token);
  if (!panelDescriptor) {
    return;
  }

  const props = getPanelProps(editorAPI, appData, options);
  props.token = token;

  editorAPI.panelManager.updatePanelProps(panelDescriptor.name, props);
}

function closePanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  value: AnyFixMe,
) {
  editorAPI.store.dispatch(
    stateManagement.panels.actions.closePlatformPanel(
      token,
      value,
      'apiMethodCall',
    ),
  );
}

function setAppAPI(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: any,
) {
  const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
  if (options?.apiName) {
    platformAPI.applications.setAppPublicAPI(options.apiName);
  }
  // migrating from using applicationId as apiName to more specific name
  // needed for compatibility with older sdk versions
  // TODO: remove once verified no apps are effected
  return platformAPI.applications.setAppPublicAPI(
    appData.applicationId as AnyFixMe,
  );
}

function openPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  panelName: string,
  panelProps: AnyFixMe,
  leavePanelsOpen: AnyFixMe,
) {
  editorAPI.panelManager.openPanel(panelName, panelProps, leavePanelsOpen);
}

function getCompRefByWidgetId(
  editorAPI: EditorAPI,
  widgetId: string,
  appDefinitionId: string,
) {
  const applicationId =
    editorAPI.dsRead.tpa.app.getDataByAppDefId(appDefinitionId)?.applicationId;
  if (!applicationId) {
    throw new Error(
      `Could not find application data for appDefinitionId: ${appDefinitionId}`,
    );
  }
  const components = experiment.isOpen('se_appDefIdUpdates')
    ? // @ts-expect-error
      editorAPI.dsRead.tpaV2.app.getAllComps(applicationId)
    : // @ts-expect-error
      editorAPI.dsRead.tpa.app.getAllCompsByApplicationId(
        applicationId as number,
      );

  const widgetCompId = components?.find(
    (c: AnyFixMe) => c.widgetId === widgetId,
  )?.id;
  if (!widgetCompId) {
    throw new Error(
      `Could not find component with widgetId: ${widgetId} for applicationId: ${applicationId}`,
    );
  }
  return editorAPI.components.get.byId(widgetCompId);
}

function getCompRefFromOptions(
  editorAPI: EditorAPI,
  appData: AppData,
  options:
    | { widgetId: string; appDefinitionId?: string }
    | { compRef: ComponentRef }
    | AnyFixMe,
): ComponentRef {
  const compRef: ComponentRef =
    options.compRef ??
    getCompRefByWidgetId(
      editorAPI,
      options.widgetId,
      options.appDefinitionId ?? appData.appDefinitionId, // options.appDefinitionId may be used for opening widget of external apps
    );
  return compRef;
}

function openSettingsPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  options: {
    widgetId: string;
    appDefinitionId?: string;
    compRef?: ComponentRef;
  },
) {
  const compRef = getCompRefFromOptions(editorAPI, appData, options);
  navigateAndOpenPanel(editorAPI, compRef, 'settings');
}

function closeTpaSettingsPanel(editorAPI: EditorAPI, compRef: ComponentRef) {
  const openedPanel = editorAPI.panelManager
    .getOpenPanels()
    .find(
      (panel: AnyFixMe) =>
        panel.props?.origCompId === compRef.id &&
        panel.name === 'tpa.compPanels.tpaSettings',
    );
  if (!openedPanel) {
    return;
  }
  editorAPI.panelManager.closePanelByName(openedPanel.name);
}

function closeNativeSettingsPanel(editorAPI: EditorAPI, compRef: ComponentRef) {
  const openedPanel = editorAPI.panelManager
    .getOpenPanels()
    .find(
      (panel: AnyFixMe) =>
        panel.componentRef?.id === compRef.id &&
        panel.name.endsWith('settingsPanel'),
    );
  if (!openedPanel) {
    return;
  }
  editorAPI.panelManager.closePanelByName(openedPanel.name);
}

function closeSettingsPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  options: {
    widgetId: string;
    appDefinitionId?: string;
    compRef?: ComponentRef;
  },
) {
  const compRef = getCompRefFromOptions(editorAPI, appData, options);
  if (editorAPI.isTpa(editorAPI.components.getType(compRef))) {
    closeTpaSettingsPanel(editorAPI, compRef);
  } else {
    closeNativeSettingsPanel(editorAPI, compRef);
  }
}

function openComponentPanelFromType(
  editorAPI: EditorAPI,
  componentType: string,
  panelName: keyof typeof constants.componentPanels,
) {
  editorAPI.store.dispatch(
    (stateManagement.panels.actions as AnyFixMe).openComponentPanelFromType(
      componentType,
      constants.componentPanels[panelName],
    ),
  );
}

function _OpenSiteMembersSettingsPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  panelProps: string,
) {
  editorAPI.store.dispatch(
    updateOrOpenPanel(
      'panels.focusPanels.siteMembersSettingsPanel',
      panelProps,
      true,
    ),
  );
}

function showUserActionNotification(
  editorAPI: EditorAPI,
  appData: PlatformAppData,
  token: string,
  {
    title,
    message,
    type,
    link,
    shouldTranslate,
  }: ShowUserActionNotificationOptions,
  linkOnClickHandler?: () => void,
): Promise<boolean> | undefined {
  const notificationInstancesOnScreen: string = _.findKey(
    editorAPI.store.getState().notifications,
    ['message', message],
  );
  if (notificationInstancesOnScreen) {
    return;
  }
  return new Promise((resolve) => {
    const notification: Notification = {
      name: 'notifications.editorNotification',
      onClose: () => resolve(false),
      title,
      message,
      type,
      id: _.uniqueId('notification-'),
      shouldTranslate: shouldTranslate || false,
    } as AnyFixMe;
    if (link?.caption) {
      linkOnClickHandler = linkOnClickHandler || link.onClick;
      notification.linkAction = {
        caption: link.caption,
        onClick: () => {
          if (_.isFunction(linkOnClickHandler)) {
            linkOnClickHandler();
          }
          resolve(true);
        },
      };
    }

    editorAPI.store.dispatch(
      stateManagement.notifications.actions.showTPAUserActionNotification(
        notification,
      ),
    );
  });
}

function showPresetUserActionNotification(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  { preset }: AnyFixMe,
) {
  switch (preset) {
    case constants.NOTIFICATIONS.PRESETS.REPEATER_MAX_ITEMS_EDITOR_LIMITATION:
      const link = {
        caption: 'PLATFORM_Live_Preview_Notification_Limit_To_50_cta',
        onClick: () => editorAPI.preview.togglePreview(),
      };
      return showUserActionNotification(editorAPI, appData as AnyFixMe, token, {
        title: '',
        message: 'PLATFORM_Live_Preview_Notification_Limit_To_50_Text',
        type: constants.NOTIFICATIONS.TYPES.INFO as AnyFixMe,
        link,
        shouldTranslate: true,
      });
    default:
      break;
  }
}

function openBillingPage(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  { premiumIntent, referrer }: AnyFixMe = {},
) {
  const isHandlerFlow = true;
  return import('@/tpa').then((tpa) => {
    tpa.packagePicker.startUpgradeFlow(
      editorAPI,
      appData.appDefinitionId,
      isHandlerFlow,
      {
        premiumIntent,
        origin: referrer,
        shouldNavigate: false,
      },
    );
  });
}

const updateAppState = (
  editorAPI: EditorAPI,
  appData: AppData,
  partialState: AnyFixMe,
) => {
  editorAPI.store.dispatch(
    stateManagement.appsStore.actions.updateAppsStore(
      appData.appDefinitionId,
      partialState,
    ),
  );
};

function openDashboardPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: string,
  options: AnyFixMe,
) {
  return editorAPI.platform.panels.openDashboardPanel(appData, options);
}

function openProgressBar(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: string,
  options: AnyFixMe,
) {
  return new Promise((resolve: AnyFixMe, reject) => {
    try {
      editorAPI.panelHelpers.openProgressBar(
        {
          title: options.title,
          totalSteps: options.totalSteps,
          currentStep: options.currentStep,
          illustration: options.image,
          taskDisplayName: options.stepTitle,
        },
        false,
        appData?.appDefinitionId,
      );
    } catch (e) {
      reject(e);
    }
    resolve();
  });
}

function updateProgressBar(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: string,
  options: AnyFixMe,
) {
  return new Promise((resolve: AnyFixMe, reject) => {
    try {
      editorAPI.panelHelpers.updateProgressBar(
        options.currentStep,
        options.stepTitle,
        options.title,
        appData?.appDefinitionId,
      );
    } catch (e) {
      reject(e);
    }
    resolve();
  });
}

function closeProgressBar(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: string,
  options: AnyFixMe = {},
) {
  return editorAPI.panelHelpers.closeProgressBar(
    options.isError,
    appData?.appDefinitionId,
  );
}

function openErrorPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: string,
  options: OpenErrorPanelOptions,
) {
  return editorAPI.platform.panels.openErrorPanel(
    options,
    appData.appDefinitionId,
  );
}

function openConfirmationPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: string,
  options: OpenConfirmationPanelOptions,
) {
  return editorAPI.platform.panels.openConfirmationPanel(options);
}

function openPromotionalPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: string,
  options: OpenPromotionalPanelOptions,
) {
  return editorAPI.platform.panels.openPromotionalPanel(options);
}

function openManageContentPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: AnyFixMe,
) {
  return new Promise(function (resolve) {
    const panelProps = {
      title: options.title,
      descriptionText: options.descriptionText,
      mainActionText: options.mainActionText,
      secondaryActionText: options.secondaryActionText,
      helpId: options.helpId,
      onPanelClose: resolve,
      origin: options.origin,
    };

    editorAPI.store.dispatch(
      openPlatformPanel({
        panelName: 'platformPanels.platformManageContentPanel',
        panelProps,
        panelMetaData: {
          frameType: constants.PANEL_TYPES.COMP,
          closeAllOtherPanels: true,
        },
      }),
    );
  });
}

function openAddElementsPanel(
  editorAPI: EditorAPI,
  appData: AppData,
  token: string,
  options: AnyFixMe,
) {
  const panelProps = {
    widgetRef: options.widgetRef,
    components: options.components,
    shouldHaveReset: options.shouldHaveReset,
    applicationId: appData.applicationId,
    type: constants.ROOT_COMPS.GFPP.ACTIONS.ADD,
  };

  editorAPI.selection.selectComponentByCompRef(
    options.compToSelect || options.widgetRef,
  );
  editorAPI.panelManager.openComponentPanel(
    'compPanels.panels.Widget.platformAddElementsPanel',
    panelProps,
  );
}

function openElementsPanel(
  editorAPI: EditorAPI,
  appData: PlatformAppData,
  token: string,
  options: AddElementsPanelOptions,
  addComponentHandler: AddComponentHandler,
  removeComponentHandler: RemoveComponentHandler,
) {
  const panelProps = {
    ...editorUtils.getElementsPanelPropsFromOptions(options),
    applicationId: appData.applicationId,
    addComponentHandler,
    removeComponentHandler,
  };
  // the panel should be open on the controlled parent of the widget (ref component)
  const compRefToSelect = editorAPI.components.is.controlledByParent(
    options.widgetRef,
  )
    ? editorAPI.components.getContainer_DEPRECATED_BAD_PERFORMANCE(
        options.widgetRef,
      )
    : options.widgetRef;

  editorAPI.selection.selectComponentByCompRef(compRefToSelect);
  editorAPI.panelManager.openComponentPanel(
    'compPanels.panels.Widget.elementsPanel',
    panelProps,
  );
}

const openTpaSettingsModal = (
  editorAPI: EditorAPI,
  _appData: AppData,
  _token: string,
  options: AnyFixMe,
) => {
  editorAPI.panelManager.openPanel(
    tpaConstants.TPA_SETTING_MODAL,
    options,
    true,
  );
};

const sections = {
  addSectionToPage: (
    editorAPI: EditorAPI,
    _appData: AppData,
    _token: string,
    addWidgetCallback: (sectionRef: CompRef) => Promise<CompRef>,
    options: { sectionName?: string; pageRef?: CompRef },
  ) =>
    sectionsService.addSectionForWidgetToEndOfPage(
      editorAPI,
      addWidgetCallback,
      options,
    ),
  getPageSectionsSortedByStageOrder: (
    editorAPI: EditorAPI,
    targetPage: CompRef,
  ) =>
    sectionsUtil.isSectionsEnabled()
      ? editorAPI.sections.getPageSectionsSortedByStageOrder(targetPage)
      : [],
};

function updatePendingAppsNotification(editorAPI: EditorAPI) {
  pendingAppsService.updateNotification(editorAPI);
}

function saveInBackground(
  editorAPI: EditorAPI,
  _appData: AppData,
  _token: string,
  options: AnyFixMe,
) {
  return stateManagement.platform.actions.saveInBackground(
    editorAPI,
    options?.isRemove ?? false,
  );
}

function getSiteScale(editorAPI: EditorAPI, _appData: AppData, _token: string) {
  return editorAPI.getSiteScale();
}

function setAutosave(
  editorAPI: EditorAPI,
  _appData: AppData,
  _token: string,
  enabled: boolean,
) {
  return editorAPI.autosaveManager.init({ enabled });
}

function getStyleDefinitionFromManifest(
  editorAPI: EditorAPI,
  _appData: AppData,
  _token: string,
  options: { editorPointer: AnyFixMe },
) {
  const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);

  return platformAPI.manifest.get.style(options.editorPointer);
}

function onStylesReady(editorAPI: EditorAPI, sdkVersion: string) {
  editorAPI.panelManager.updatePanelProps(tpaConstants.TPA_SETTINGS_PANEL_ID, {
    sdkVersion,
    isAlive: true,
    stylesReady: true,
  });
}

function onAppEngaged(
  editorAPI: EditorAPI,
  params: {
    app_id?: string;
    instance_id: string;
    premium_intent?: string;
  },
) {
  editorAPI.bi.event(bi.events.APP_ENGAGEMENT, params);
}

function openSiteMembersSettingsDialog(editorAPI: EditorAPI) {
  editorAPI.panelManager.openPanel(
    'panels.focusPanels.siteMembersSettingsPanel',
    {},
    true,
  );
}

function getPanelNamesToCloseByTarget(target: string): string[] {
  switch (target.toLowerCase()) {
    case 'all':
      return [
        tpaConstants.TPA_SETTINGS_PANEL_ID,
        tpaConstants.TPA_SETTING_MODAL,
        platform.constants.panelTypes.DASHBOARD,
      ];
    case 'settings':
      return [tpaConstants.TPA_SETTINGS_PANEL_ID];
    case 'modal':
    default:
      return [
        tpaConstants.TPA_SETTING_MODAL,
        platform.constants.panelTypes.DASHBOARD,
      ];
  }
}

function closePanelsByTarget(
  editorAPI: EditorAPI,
  appData: AppData,
  compRef: CompRef,
  message: AnyFixMe,
) {
  const target = message?.target ?? '';
  const openedPanels = editorAPI.panelManager.getOpenPanels();
  const panelsToClose = getPanelNamesToCloseByTarget(target).filter(
    (panelName) => openedPanels.some((panel) => panel.name === panelName),
  );

  panelsToClose.forEach((panelName) => {
    editorAPI.panelManager.updatePanelProps(panelName, {
      shouldCloseModal: true,
      closeModalMessage: message,
    });
  });
}

function openAppMarketModal(
  editorAPI: EditorAPI,
  _appData: AppData,
  _token: string,
  options: {
    url: string;
    height: number;
    width: number;
    shouldHideHeader: boolean;
  },
) {
  appMarketUtils.openAppMarketModal(
    editorAPI.panelManager.openPanel,
    tpaConstants.MODALS.MARKET,
    { title: 'app_market_title', ...options },
  );
}

function openBackgroundPicker(
  editorAPI: EditorAPI,
  _appData: AppData,
  _token: string,
  options: {
    position: Point;
    color: string;
    onChange: AnyFixMe;
    onClose: AnyFixMe;
  },
) {
  const { position, color, onChange, onClose } = options;

  editorAPI.panelManager.openPanel(
    'panels.toolPanels.stylableEditor.fillPickerPanel',
    {
      title: translate('StylablePanel_FillPicker_Main_Header'),
      value: color,
      tab: color.includes('gradient') ? 1 : 0,
      onChange,
      panelHost: {
        experiments: {
          isNewPalette: isNewColorPaletteOpen(editorAPI),
        },
        visibleThemeColors: editorAPI.theme.colors.getVisibleThemeColorsKeys(),
        translate,
        getLinkedThemeColorFromColorDefault:
          editorAPI.theme.colors.getLinkedThemeColorFromColorDefault,
      },
      onClose,
      top: position.y,
      left: position.x,
      border: true,
      pickerPropOverrides: { color: { minimalMode: true } },
    },
    true,
  );
}

const setTpaSettingsHelpArticle = (
  editorAPI: EditorAPI,
  _appData: AppData,
  _token: string,
  { type, articleId }: { articleId: string; type: string },
) => {
  if (type === 'MODAL') {
    editorAPI.panelManager.updatePanelProps('tpa.compModals.tpaSettingsModal', {
      articleId,
    });
  } else {
    editorAPI.panelManager.updatePanelProps('tpa.compPanels.tpaSettings', {
      articleId,
    });
  }
};

const addAppToTrackForUpgrade = (
  editorAPI: EditorAPI,
  applicationId: number,
) => {
  editorAPI.store.dispatch(
    stateManagement.tpa.actions.addAppToTrackForUpgrade(applicationId),
  );
};

const getCompRefFromCompId = (editorAPI: EditorAPI, compId: string) =>
  typeof compId === 'string'
    ? editorAPI.dsRead.components.get.byId(compId)
    : compId;

const showSavePublishFailAlert = (editorAPI: EditorAPI, appData: AppData) => {
  tpaAlertsService.openSaveFailedAlertForDashboardUrl(
    editorAPI,
    appData.appDefinitionId,
  );
};

export {
  getCompRefFromCompId,
  openPanel,
  getStyleDefinitionFromManifest,
  _OpenSiteMembersSettingsPanel,
  openChangeVariationsPanel,
  openComponentPanel,
  openComponentPanelWithSiteContext,
  openSettingsPanel,
  closeSettingsPanel,
  createOpenTpaComponentPanelHandler,
  openComponentPanelFromType,
  getCompRefByWidgetId,
  hidePanelPreloader,
  showPanelPreloader,
  setTpaSettingsHelpArticle,
  openNativeComponentPanel,
  openModalPanel,
  openToolPanel,
  openFloatingToolPanel,
  openPagePanel,
  openPagesPanel,
  openTpaSettingsModal,
  closePagesPanel,
  canAddStaticPage,
  openFullStagePanel,
  openManageContentPanel,
  openLinkPanel,
  openSidePanel,
  openColorPicker,
  openFontPicker,
  openBackgroundPicker,
  openMediaPanel,
  openHelpPanel,
  showSavePublishFailAlert,
  updatePanelOptions,
  closePanel,
  setAppAPI,
  editorUtils as utils,
  showUserActionNotification,
  showPresetUserActionNotification,
  openBillingPage,
  updateAppState,
  openDashboardPanel,
  openAppMarketModal,
  openProgressBar,
  updateProgressBar,
  closeProgressBar,
  openErrorPanel,
  openConfirmationPanel,
  openPromotionalPanel,
  openAddElementsPanel,
  openElementsPanel,
  sections,
  updatePendingAppsNotification,
  saveInBackground,
  getSiteScale,
  setAutosave,
  onStylesReady,
  onAppEngaged,
  addAppToTrackForUpgrade,
  closePanelsByTarget,
  openSiteMembersSettingsDialog,
};
